import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Route, Routes, useLocation } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import './App.css';
import Navbar from './components/Navbar';
import { DropdownProvider } from "./contexts/DropdownContext";
import ScrollToTop from './components/ScrollToTop';
import { AnimatePresence, motion } from 'framer-motion';
import ProtectedRoute from "./utils/ProtectedRoute";
import useAuthNavigation from './contexts/useAuthNavigation';
//analytics
import usePageTracking from "./hooks/usePageTracking";
import AboutUsPage from '../src/pages/AboutUsPage';


const HomePage = lazy(() => import('../src/pages/HomePageNew'));
const ServicePage = lazy(() => import('../src/pages/ServicePage'));
const DestinationsPage = lazy(() => import('../src/pages/DestinationsPage'));
const EventsNewsPage = lazy(() => import('../src/pages/Events&News'));
const MindfulnessPage = lazy(() => import('../src/pages/MindfulnessPage'));
const MindfulnessInnerPage = lazy(() => import('../src/pages/MindfulnessInnerPage'));
const ContactUsPage = lazy(() => import('../src/pages/ContactUsPage'));
const BookNowPage = lazy(() => import('../src/pages/BookNowPage'));
const BlogsPage = lazy(() => import('../src/pages/BlogsPage'));
const BlogsContentPage = lazy(() => import('../src/pages/BlogsContentPage'));
const PackageContent = lazy(() => import('../src/pages/PackageContent'));
const SriLankanToursPage = lazy(() => import('../src/pages/SriLankanToursPage'));
const MaldivesToursPage = lazy(() => import('../src/pages/MaldivesToursPage'));
const DubaiToursPage = lazy(() => import('../src/pages/DubaiToursPage'));
const IndonesiaToursPage = lazy(() => import('../src/pages/IndonesiaToursPage'));
const SingaporeToursPage = lazy(() => import('../src/pages/SingaporeToursPage'));
const ItineraryPage = lazy(() => import('../src/pages/ItineraryPage'));
const EventsInnerPage = lazy(() => import('../src/pages/EventsInnerPage'));
const TermsConditionsPage = lazy(() => import('../src/pages/Terms&ConditionsPage'));
const PrivacyPolicyPage = lazy(() => import('../src/pages/PrivacyPolicyPage'));

const AdminLoginPage = lazy(() => import('../src/components/Admin/AdminLogin'));
const AdminDashboard = lazy(() => import('../src/pages/AdminDashboard'));

const MaggaPage = lazy(() => import('../src/pages/MaggaPage'));
const BookFlightsPage = lazy(() => import('../src/pages/BookFlightsPage'));
const DMCExcellencePage = lazy(() => import('../src/pages/DMCExcellencePage'));
const DMCServicePage = lazy(() => import('../src/pages/DMCServicePage'));
const MiceServicesPage = lazy(() => import('../src/pages/MiceServicesPage'));
const INTTAdvantagePage = lazy(() => import('../src/pages/INTTAdvantagePage'));
const TipsPage = lazy(() => import('../src/pages/TipsPage'));

const pageVariants = {
  initial: {
    x: "100%",
    opacity: 1,
    position: "absolute",
  },
  enter: {
    x: 0,
    opacity: 1,
    position: "absolute",
    transition: {
      duration: 0.5,
      ease: "easeInOut"
    }
  },
  exit: {
    x: "-100%",
    opacity: 1,
    position: "absolute",
    transition: {
      duration: 0.5,
      ease: "easeInOut"
    }
  },
};

const App = () => {
  const location = useLocation();
  const { isAuthenticated, setIsAuthenticated } = useAuthNavigation();
  //analytics
  usePageTracking();

  const hideNavbarPaths = ["/admin", "/admin/dashboard"];

  const shouldHideNavbar = hideNavbarPaths.includes(location.pathname);

  return (
    <div className="main-container">
      <ScrollToTop />
      {/* Conditionally render the Navbar based on the current route */}
      <DropdownProvider>
        {!shouldHideNavbar && <Navbar />}
        <AnimatePresence>
          <Suspense fallback={<div>Loading...</div>}>
            <Routes location={location} key={location.pathname}>
              <Route
                path="/services"
                element={
                  <motion.div initial="initial" animate="enter" exit="exit" variants={pageVariants} className="page-transition">
                    <ServicePage />
                  </motion.div>
                }
              />
              <Route
                path="/mindfulness"
                element={
                  <motion.div initial="initial" animate="enter" exit="exit" variants={pageVariants} className="page-transition">
                    <MindfulnessPage />
                  </motion.div>
                }
              />
              <Route
                path="/mindfulness/:route"
                element={
                  <motion.div initial="initial" animate="enter" exit="exit" variants={pageVariants} className="page-transition">
                    <MindfulnessInnerPage />
                  </motion.div>
                }
              />
              <Route
                path="/destinations"
                element={
                  <motion.div initial="initial" animate="enter" exit="exit" variants={pageVariants} className="page-transition">
                    <DestinationsPage />
                  </motion.div>
                }
              />
              <Route
                path="/why-choose-us"
                element={
                  <motion.div initial="initial" animate="enter" exit="exit" variants={pageVariants} className="page-transition">
                    <AboutUsPage />
                  </motion.div>
                }
              />
              <Route
                path="/contact-us"
                element={
                  <motion.div initial="initial" animate="enter" exit="exit" variants={pageVariants} className="page-transition">
                    <ContactUsPage />
                  </motion.div>
                }
              />
              <Route
                path="/events-news"
                element={
                  <motion.div initial="initial" animate="enter" exit="exit" variants={pageVariants} className="page-transition">
                    <EventsNewsPage />
                  </motion.div>
                }
              />
              <Route
                path="/events-news/:route"
                element={
                  <motion.div initial="initial" animate="enter" exit="exit" variants={pageVariants} className="page-transition">
                    <EventsInnerPage />
                  </motion.div>
                }
              />
              <Route
                path="/book-now"
                element={
                  <motion.div initial="initial" animate="enter" exit="exit" variants={pageVariants} className="page-transition">
                    <BookNowPage />
                  </motion.div>
                }
              />
              <Route
                path="/blogs"
                element={
                  <motion.div initial="initial" animate="enter" exit="exit" variants={pageVariants} className="page-transition">
                    <BlogsPage />
                  </motion.div>
                }
              />
              <Route
                path="/blogs/:route"
                element={
                  <motion.div initial="initial" animate="enter" exit="exit" variants={pageVariants} className="page-transition">
                    <BlogsContentPage />
                  </motion.div>
                }
              />
              <Route
                path="/services/:route"
                element={
                  <motion.div initial="initial" animate="enter" exit="exit" variants={pageVariants} className="page-transition">
                    <PackageContent />
                  </motion.div>
                }
              />
              <Route
                path="/destinations/sri-lanka"
                element={
                  <motion.div initial="initial" animate="enter" exit="exit" variants={pageVariants} className="page-transition">
                    <SriLankanToursPage />
                  </motion.div>
                }
              />
              {/* <Route
              path="/destinations/:country/:category/:route"
              element={
                <motion.div initial="initial" animate="enter" exit="exit" variants={pageVariants} className="page-transition">
                  <ItineraryPage />
                </motion.div>
              }
            /> */}
              <Route
                path="/destinations/dubai"
                element={
                  <motion.div initial="initial" animate="enter" exit="exit" variants={pageVariants} className="page-transition">
                    <DubaiToursPage />
                  </motion.div>
                }
              />
              <Route
                path="/destinations/indonesia"
                element={
                  <motion.div initial="initial" animate="enter" exit="exit" variants={pageVariants} className="page-transition">
                    <IndonesiaToursPage />
                  </motion.div>
                }
              />
              <Route
                path="/destinations/maldives"
                element={
                  <motion.div initial="initial" animate="enter" exit="exit" variants={pageVariants} className="page-transition">
                    <MaldivesToursPage />
                  </motion.div>
                }
              />
              <Route
                path="/destinations/singapore"
                element={
                  <motion.div initial="initial" animate="enter" exit="exit" variants={pageVariants} className="page-transition">
                    <SingaporeToursPage />
                  </motion.div>
                }
              />
              <Route
                path="/destinations/:country/:category/:route"
                element={
                  <motion.div initial="initial" animate="enter" exit="exit" variants={pageVariants} className="page-transition">
                    <ItineraryPage />
                  </motion.div>
                }
              />
              <Route
                path="/tips/:route"
                element={
                  <motion.div initial="initial" animate="enter" exit="exit" variants={pageVariants} className="page-transition">
                    <TipsPage />
                  </motion.div>
                }
              />
              <Route
                path="/termsConditions"
                element={
                  <motion.div initial="initial" animate="enter" exit="exit" variants={pageVariants} className="page-transition">
                    <TermsConditionsPage />
                  </motion.div>
                }
              />
              <Route
                path="/privacyPolicy"
                element={
                  <motion.div initial="initial" animate="enter" exit="exit" variants={pageVariants} className="page-transition">
                    <PrivacyPolicyPage />
                  </motion.div>
                }
              />
              <Route
                path="/"
                element={
                  <motion.div initial="initial" animate="enter" exit="exit" variants={pageVariants} className="page-transition">
                    <HomePage />
                  </motion.div>
                }
              />
              <Route
                path="/admin"
                element={
                  <motion.div initial="initial" animate="enter" exit="exit" variants={pageVariants} className="page-transition">
                    <AdminLoginPage setIsAuthenticated={setIsAuthenticated} />
                  </motion.div>
                }
              />
              <Route
                path="/admin/dashboard"
                element={
                  <ProtectedRoute isAuthenticated={isAuthenticated}>
                    <motion.div initial="initial" animate="enter" exit="exit" variants={pageVariants} className="page-transition">
                      <AdminDashboard />
                    </motion.div>
                  </ProtectedRoute>
                }
              />









              <Route
                path="/magga"
                element={
                  <motion.div initial="initial" animate="enter" exit="exit" variants={pageVariants} className="page-transition">
                    <MaggaPage />
                  </motion.div>
                }
              />

              <Route
                path="/book-flights"
                element={
                  <motion.div initial="initial" animate="enter" exit="exit" variants={pageVariants} className="page-transition">
                    <BookFlightsPage />
                  </motion.div>
                }
              />

              <Route
                path="/dmc-excellence"
                element={
                  <motion.div initial="initial" animate="enter" exit="exit" variants={pageVariants} className="page-transition">
                    <DMCExcellencePage />
                  </motion.div>
                }
              />

              <Route
                path="/dmc-services"
                element={
                  <motion.div initial="initial" animate="enter" exit="exit" variants={pageVariants} className="page-transition">
                    <DMCServicePage />
                  </motion.div>
                }
              />

              <Route
                path="/mice-services"
                element={
                  <motion.div initial="initial" animate="enter" exit="exit" variants={pageVariants} className="page-transition">
                    <MiceServicesPage />
                  </motion.div>
                }
              />

              <Route
                path="/intt-advantage"
                element={
                  <motion.div initial="initial" animate="enter" exit="exit" variants={pageVariants} className="page-transition">
                    <INTTAdvantagePage />
                  </motion.div>
                }
              />

            </Routes>
          </Suspense>
        </AnimatePresence>
      </DropdownProvider>
    </div>
  );
};

export default App;
